import { useEffect, useRef, useState } from "react";
import { signOut } from "firebase/auth";
import { auth } from "../firebase/firebaseConfig";
import { Capacitor } from "@capacitor/core";

interface UseSessionTimeoutOptions {
  /** Tiempo de inactividad en minutos antes de cerrar sesión (default: 30) */
  inactivityTimeout?: number;
  /** Tiempo total de sesión en horas antes de forzar re-login (default: 1) */
  maxSessionDuration?: number;
  /** Callback cuando la sesión está por expirar */
  onWarning?: (minutesLeft: number) => void;
  /** Callback cuando la sesión expira */
  onExpire?: () => void;
}

export function useSessionTimeout(options: UseSessionTimeoutOptions = {}) {
  const {
    inactivityTimeout = 30, // 30 minutos de inactividad
    maxSessionDuration = 1, // 1 hora máximo (cambiado de 24)
    onWarning,
    onExpire,
  } = options;

  const isAdmin = typeof window !== 'undefined' && window.location.pathname.includes('admin.html');
  const startKey = isAdmin ? "admin_session_start_time" : "session_start_time";
  const activityKey = isAdmin ? "admin_last_activity_time" : "last_activity_time";

  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxSessionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem(startKey);
      localStorage.removeItem(activityKey);
      if (onExpire) onExpire();
    } catch (error) {
    }
  };

  const resetInactivityTimer = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    inactivityTimerRef.current = setTimeout(() => {
      handleLogout();
    }, inactivityTimeout * 60 * 1000);
  };

  const markActivity = () => {
    try {
      localStorage.setItem(activityKey, Date.now().toString());
    } catch {
    }
  };

  const startWarningTimer = () => {
    const warningTime = (inactivityTimeout - 5) * 60 * 1000;
    if (warningTime > 0) {
      warningTimerRef.current = setTimeout(() => {
        setTimeLeft(5);
        if (onWarning) onWarning(5);
      }, warningTime);
    }
  };

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const isIos = (() => {
      try {
        return Capacitor.getPlatform() === 'ios';
      } catch {
        return false;
      }
    })();

    // Guardar tiempo de inicio de sesión
    let sessionStartTime = localStorage.getItem(startKey);
    if (!sessionStartTime) {
      sessionStartTime = Date.now().toString();
      localStorage.setItem(startKey, sessionStartTime);
    }

    // Si pasó demasiado tiempo desde la última actividad (incluso entre recargas),
    // forzar re-login al volver.
    try {
      const last = localStorage.getItem(activityKey);
      if (last) {
        const elapsedMs = Date.now() - parseInt(last);
        if (elapsedMs >= inactivityTimeout * 60 * 1000) {
          handleLogout();
          return;
        }
      }
    } catch {
    }

    // Verificar si la sesión ya expiró por tiempo máximo
    const startTime = parseInt(sessionStartTime);
    const elapsedHours = (Date.now() - startTime) / (1000 * 60 * 60);
    if (!isIos && elapsedHours >= maxSessionDuration) {
      handleLogout();
      return;
    }

    // Timer de sesión máxima
    const remainingTime = maxSessionDuration * 60 * 60 * 1000 - (Date.now() - startTime);
    if (!isIos) {
      maxSessionTimerRef.current = setTimeout(() => {
        handleLogout();
      }, remainingTime);
    }

    // Timer de inactividad
    resetInactivityTimer();
    startWarningTimer();
    markActivity();

    const events = ["mousedown", "keydown", "scroll", "touchstart", "click"];
    const handleActivity = () => {
      markActivity();
      resetInactivityTimer();
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      startWarningTimer();
    };

    events.forEach((event) => window.addEventListener(event, handleActivity));

    return () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (maxSessionTimerRef.current) clearTimeout(maxSessionTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      events.forEach((event) => window.removeEventListener(event, handleActivity));
    };
  }, [inactivityTimeout, maxSessionDuration]); // eslint-disable-line react-hooks/exhaustive-deps

  return { timeLeft };
}
